import type { MazeCell, MazeConfig, MazePuzzle, MazeSize } from '../../types'
import { MAZE_DIMENSIONS } from '../../types'

// ─── Types ────────────────────────────────────────────────────────────────────

type WallKey = keyof MazeCell  // 'top' | 'right' | 'bottom' | 'left'
type Pos     = [number, number] // [row, col]

const OPPOSITE: Record<WallKey, WallKey> = {
  top:    'bottom',
  bottom: 'top',
  left:   'right',
  right:  'left',
}

const DIRS: { dr: number; dc: number; wall: WallKey }[] = [
  { dr: -1, dc:  0, wall: 'top'    },
  { dr:  1, dc:  0, wall: 'bottom' },
  { dr:  0, dc: -1, wall: 'left'   },
  { dr:  0, dc:  1, wall: 'right'  },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function initCells(rows: number, cols: number): MazeCell[][] {
  return Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => ({ top: true, right: true, bottom: true, left: true })),
  )
}

// ─── Maze generation — iterative DFS (recursive backtracking) ────────────────

function carve(cells: MazeCell[][], rows: number, cols: number): void {
  const visited = new Uint8Array(rows * cols)
  const stack: Pos[] = []

  const startRow = Math.floor(Math.random() * rows)
  const startCol = Math.floor(Math.random() * cols)
  visited[startRow * cols + startCol] = 1
  stack.push([startRow, startCol])

  while (stack.length > 0) {
    const [r, c] = stack[stack.length - 1]

    // Find unvisited neighbours in a random order
    const neighbours = shuffle(DIRS)
      .map(({ dr, dc, wall }) => {
        const nr = r + dr, nc = c + dc
        return { nr, nc, wall, valid: nr >= 0 && nr < rows && nc >= 0 && nc < cols && !visited[nr * cols + nc] }
      })
      .filter((n) => n.valid)

    if (neighbours.length === 0) {
      stack.pop()
    } else {
      const { nr, nc, wall } = neighbours[0]
      // Remove the shared wall
      cells[r][c][wall]               = false
      cells[nr][nc][OPPOSITE[wall]]   = false
      visited[nr * cols + nc]         = 1
      stack.push([nr, nc])
    }
  }
}

// ─── Solution — BFS from entry to exit ───────────────────────────────────────

function bfsSolve(
  cells:    MazeCell[][],
  rows:     number,
  cols:     number,
  entryCol: number,
  exitCol:  number,
): Pos[] {
  const key  = (r: number, c: number) => r * cols + c
  const prev = new Int32Array(rows * cols).fill(-1)  // stores key of parent, -1 = unvisited
  const start = key(0, entryCol)

  prev[start] = start  // sentinel: start points to itself
  const queue: number[] = [start]

  while (queue.length > 0) {
    const cur = queue.shift()!
    const r = Math.floor(cur / cols), c = cur % cols

    if (r === rows - 1 && c === exitCol) {
      // Reconstruct path
      const path: Pos[] = []
      let k = cur
      while (k !== key(0, entryCol)) {
        path.unshift([Math.floor(k / cols), k % cols])
        k = prev[k]
      }
      path.unshift([0, entryCol])
      return path
    }

    for (const { dr, dc, wall } of DIRS) {
      const nr = r + dr, nc = c + dc
      const nk = key(nr, nc)
      if (nr >= 0 && nr < rows && nc >= 0 && nc < cols && !cells[r][c][wall] && prev[nk] === -1) {
        prev[nk] = cur
        queue.push(nk)
      }
    }
  }

  return [] // unreachable in a perfect maze
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Check if moving from `from` in the direction of `to` is wall-free */
export function canMove(cells: MazeCell[][], from: Pos, to: Pos): boolean {
  const [fr, fc] = from
  const [tr, tc] = to
  const dr = tr - fr, dc = tc - fc
  if (Math.abs(dr) + Math.abs(dc) !== 1) return false // must be adjacent

  const wall: WallKey =
    dr === -1 ? 'top' :
    dr ===  1 ? 'bottom' :
    dc === -1 ? 'left' :
    'right'

  return !cells[fr][fc][wall]
}

export function generateMaze(config: MazeConfig): MazePuzzle {
  const { rows, cols } = MAZE_DIMENSIONS[config.size as MazeSize]
  const cells = initCells(rows, cols)

  carve(cells, rows, cols)

  // Open entry (top row) and exit (bottom row) — choose random columns
  const entryCol = Math.floor(Math.random() * cols)
  const exitCol  = Math.floor(Math.random() * cols)
  cells[0][entryCol].top              = false
  cells[rows - 1][exitCol].bottom     = false

  const solution = bfsSolve(cells, rows, cols, entryCol, exitCol)

  return { config, rows, cols, cells, entryCol, exitCol, solution }
}
